import { ApiError, type ErrorCode, type OwnerCatalogueItemResource } from '@repo/api-client';

import type { ReadyRow } from './catalogue-upload';

/**
 * Creating the items, one request at a time.
 *
 * ## Why a client-side loop is defensible here, and nowhere else
 *
 * `POST /v1/catalogue/items` writes **the item, its first price and its outlet
 * availability in one transaction** — the route says so itself: "an item with no
 * price is an item a till cannot sell (R-203)". So there is no such thing as a
 * half-written item. A failure at row 300 leaves 300 complete, sellable items
 * and 200 not yet created, which is a *resumable* state rather than a corrupt
 * one, and that is the only reason this may be a loop in a browser. **Do not
 * generalise it to any other bulk write.**
 *
 * ## The one decision the whole ticket turns on
 *
 * **`CATALOGUE_CODE_TAKEN` is "already created, skip and continue", never a
 * failure.** A code is unique for all time within its kind and is never
 * reissued, retired or not (FR-215), so a 409 on this route does not mean the
 * file is wrong — it means that row is already done. Treating it as a failure is
 * what the obvious implementation does, and it turns the second run of a
 * partially-uploaded file into a report of 88 broken rows when the import is
 * simply already partly done.
 *
 * `412 created, 88 already existed, 0 failed` is a **successful** re-run, and
 * `countOutcomes` keeps the three apart so the screen can say so.
 *
 * ## Sequential, deliberately
 *
 * One request at a time, in file order. It keeps the API's append-only audit log
 * in the order of the file somebody is holding, and it does not open five
 * hundred connections to a server on a hill.
 *
 * ## What stops the run, and what does not
 *
 * A refusal that is **about the row** — a price the API will not carry, an
 * outlet that has gone since the review, a validation the review did not model —
 * fails that row and the run moves on. Anything else stops it at that row.
 *
 * The list is stated as the codes that *continue*, not as the codes that halt,
 * which is the safer direction: a code nobody anticipated — a lapsed session, a
 * lease problem, an idempotency-key refusal, something added to the API next
 * month — stops the run rather than repeating itself five hundred times and
 * reporting five hundred identical failures. Nothing is lost by stopping,
 * because every row already created stays created and the same file re-run picks
 * up where it left off.
 */

/** What happened to one row. */
export type RowOutcome =
  | { readonly kind: 'created'; readonly row: ReadyRow; readonly item: OwnerCatalogueItemResource }
  | { readonly kind: 'skipped'; readonly row: ReadyRow }
  | { readonly kind: 'failed'; readonly row: ReadyRow; readonly error: unknown };

/** A finished run, however it finished. */
export interface UploadRunResult {
  /** One per row attempted, in the order they were attempted. */
  readonly outcomes: readonly RowOutcome[];

  /**
   * The row the run stopped on and why, or `null` where it ran to the end.
   *
   * That row is also in `outcomes` as a failure — it was attempted and it did
   * fail — and this says additionally that nothing after it was tried.
   */
  readonly halted: { readonly at: ReadyRow; readonly error: unknown } | null;

  /** Whether the person stopped it. Distinct from having halted on a failure. */
  readonly stopped: boolean;
}

/** The three counts a result reads as. */
export interface OutcomeCounts {
  readonly created: number;

  /** Already existed. **Not a failure**, and never presented as one. */
  readonly skipped: number;

  readonly failed: number;
}

/**
 * The refusals that are about the row in hand, so the run carries on past them.
 *
 * `CATALOGUE_CODE_TAKEN` is absent because it is not a failure at all — it is
 * handled above these, as a skip.
 */
const ROW_SPECIFIC: readonly ErrorCode[] = [
  // The four the review step models, which reach here only when this app's copy
  // of the outlets and the server's have drifted between the review and the run.
  'CATALOGUE_STOCK_ON_NON_PRODUCT',
  'CATALOGUE_STOCK_NOT_AVAILABLE_HERE',
  'CATALOGUE_PRICE_INVALID',
  'RESOURCE_NOT_FOUND',

  // A field rule the review does not model. It is about this row's values and
  // nothing else, so the rest of the file is still worth attempting.
  'VALIDATION_FAILED',
];

/**
 * Create every ready row, in order, and say what became of each.
 *
 * `create` is passed in rather than built here: this module is the decision
 * about what a refusal *means*, and a test that has to stand up an API client to
 * ask it is a test of something else. The screen supplies the one that mints an
 * idempotency key per row and calls the generated operation.
 */
export async function runCatalogueUpload({
  rows,
  create,
  onOutcome,
  shouldStop,
}: {
  rows: readonly ReadyRow[];
  create: (row: ReadyRow) => Promise<OwnerCatalogueItemResource>;

  /** Called as each row lands, so a live count can be shown while it runs. */
  onOutcome?: (outcome: RowOutcome, done: number) => void;

  /**
   * Asked before each row. `true` finishes the run without attempting it.
   *
   * Asked *between* rows rather than given an `AbortSignal` for the request: a
   * create that has already reached the API may well have been written, and a
   * row this app aborted mid-flight is a row it cannot say anything true about.
   * Stopping between rows means every row is either attempted and known, or not
   * attempted at all.
   */
  shouldStop?: () => boolean;
}): Promise<UploadRunResult> {
  const outcomes: RowOutcome[] = [];

  for (const row of rows) {
    if (shouldStop?.() === true) {
      return { outcomes, halted: null, stopped: true };
    }

    let outcome: RowOutcome;

    try {
      outcome = { kind: 'created', row, item: await create(row) };
    } catch (error) {
      outcome = alreadyExists(error) ? { kind: 'skipped', row } : { kind: 'failed', row, error };
    }

    outcomes.push(outcome);
    onOutcome?.(outcome, outcomes.length);

    if (outcome.kind === 'failed' && !isAboutTheRow(outcome.error)) {
      return { outcomes, halted: { at: row, error: outcome.error }, stopped: false };
    }
  }

  return { outcomes, halted: null, stopped: false };
}

/** The three figures a result is read as. */
export function countOutcomes(outcomes: readonly RowOutcome[]): OutcomeCounts {
  const of = (kind: RowOutcome['kind']) =>
    outcomes.filter((outcome) => outcome.kind === kind).length;

  return { created: of('created'), skipped: of('skipped'), failed: of('failed') };
}

/**
 * Whether this refusal means the row is already there.
 *
 * Branched on `code` and never on the status: 409 is also
 * `CATALOGUE_STOCK_NOT_AVAILABLE_HERE` on this very route, which is a genuine
 * refusal of a row that was never written — reading the status alone would
 * report it as an item that already exists, and the file's author would never
 * learn their stock-tracked product named a gate.
 */
export function alreadyExists(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'CATALOGUE_CODE_TAKEN';
}

/**
 * Whether this failure is about the row in hand rather than about the run.
 *
 * `ApiNetworkError` is not one: nothing answered, so the rest of the file will
 * meet the same silence, and five hundred rows of "could not reach the server"
 * is worse than one and a stop. The rows already created stay created.
 */
function isAboutTheRow(error: unknown): boolean {
  return error instanceof ApiError && error.code !== undefined && ROW_SPECIFIC.includes(error.code);
}
